// Stage 5 C-A §6 — Canonical portal Overview (aka 548-02 / 562-01).
// Renders project state, outstanding actions, upcoming programme,
// current Design Issue and — when applicable — the Paid Services
// overlay (562-01).  Overview is extended, never duplicated.
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { portalApi, fmtDate, daysUntil } from "./portalApi";
import {
    PageHeader, Card, CardHeader, Pill, Button, EmptyState, Spinner,
} from "./PortalUi";
import ExecutedDocumentsTile from "./ExecutedDocumentsTile";
import { usePageMeta } from "../../components/Layout";

export default function PortalOverview() {
    usePageMeta({ title: "Overview | Studio G4 Portal" });
    const [data, setData] = useState(null);
    const [err, setErr] = useState("");
    useEffect(() => {
        portalApi.overview()
            .then(r => setData(r.data))
            .catch(e => setErr(e?.response?.data?.detail || "Unavailable"));
    }, []);
    if (err) return <div className="py-8 px-4" data-testid="portal-overview-err"><EmptyState heading="Overview unavailable" note={err} /></div>;
    if (!data) return <Spinner testid="portal-overview-loading" />;
    const p = data.project;
    const paid = data.paid_services || [];
    return (
        <div className="py-8 px-4" data-testid="portal-overview">
            <PageHeader
                title="Overview"
                testid="portal-overview-header"
                right={<Pill tone="accent" testid="portal-overview-workstage">{p.workstage_label}</Pill>}
            />
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                <Card testid="portal-overview-state-card">
                    <div className="p-4">
                        <div className="text-[10px] uppercase tracking-[0.12em] text-[#9A9A94] font-manrope font-semibold mb-1">Project status</div>
                        <div className="font-manrope text-[17px] font-semibold text-charcoal">{p.project_state}</div>
                        <div className="mt-1 text-[12px] text-[#6B6B67]">Postcode {p.postcode}</div>
                    </div>
                </Card>
                <Card testid="portal-overview-actions-card">
                    <div className="p-4">
                        <div className="text-[10px] uppercase tracking-[0.12em] text-[#9A9A94] font-manrope font-semibold mb-1">Action required</div>
                        <div className="font-manrope text-[28px] font-semibold text-charcoal leading-none" data-testid="portal-overview-outstanding">{data.outstanding_actions_count}</div>
                        <div className="mt-1 text-[12px] text-[#6B6B67]">Items awaiting your input</div>
                        {data.outstanding_actions_count > 0 && (
                            <Link to="/portal/actions" className="mt-2 inline-block text-[12px] text-[#A8543F] font-manrope font-semibold hover:underline" data-testid="portal-overview-goto-actions">
                                View actions →
                            </Link>
                        )}
                    </div>
                </Card>
                <Card testid="portal-overview-design-card">
                    <div className="p-4">
                        <div className="text-[10px] uppercase tracking-[0.12em] text-[#9A9A94] font-manrope font-semibold mb-1">Current design</div>
                        {data.current_design_issue ? (
                            <>
                                <div className="font-manrope text-[15px] font-semibold text-charcoal leading-tight">{data.current_design_issue.title}</div>
                                <div className="mt-1 text-[12px] text-[#6B6B67]">
                                    Feedback due {fmtDate(data.current_design_issue.feedback_due_date)}
                                </div>
                                <Link to="/portal/design" className="mt-2 inline-block text-[12px] text-[#A8543F] font-manrope font-semibold hover:underline" data-testid="portal-overview-goto-design">
                                    View design →
                                </Link>
                            </>
                        ) : (
                            <div className="text-[13px] text-[#6B6B67]">No current design issue.</div>
                        )}
                    </div>
                </Card>
            </div>

            {/* 562-01 Paid Services overlay — extends this same Overview */}
            {paid.length > 0 && (
                <Card className="mb-6" testid="portal-overview-paid-services">
                    <CardHeader title="Your services" subtle />
                    <div className="p-4 grid grid-cols-1 md:grid-cols-2 gap-3">
                        {paid.map(pkg => (
                            <Link key={pkg.id} to={`/portal/paid-services/packages/${pkg.id}`}
                                  className="border border-[#EEEBE4] rounded-md p-3 hover:border-[#A8543F] transition-colors"
                                  data-testid={`portal-overview-paid-${pkg.id}`}>
                                <div className="flex items-center justify-between">
                                    <div className="font-manrope font-semibold text-[14px] text-charcoal">{pkg.package_name}</div>
                                    <Pill tone={pkg.status === "Active" ? "ok" : pkg.status === "Completed" ? "neutral" : "info"}>{pkg.status_label || pkg.status}</Pill>
                                </div>
                                <div className="mt-1 text-[11.5px] text-[#8A8A84]">{pkg.contract_basis_label || "—"}</div>
                            </Link>
                        ))}
                    </div>
                </Card>
            )}

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Card testid="portal-overview-programme">
                    <CardHeader title="Upcoming programme" subtle right={<Link to="/portal/programme" className="text-[12px] text-[#A8543F] font-manrope font-semibold hover:underline">Full programme →</Link>} />
                    <div className="p-4">
                        {(!data.programme_snapshot || !data.programme_snapshot.length) ? (
                            <div className="text-[13px] text-[#6B6B67]">No upcoming items.</div>
                        ) : (
                            <ul className="divide-y divide-[#EEEBE4]">
                                {data.programme_snapshot.map(it => (
                                    <li key={it.id} className="py-2 flex items-center justify-between" data-testid={`portal-overview-prog-${it.id}`}>
                                        <span className="text-[13.5px] text-charcoal">{it.title}</span>
                                        <span className="text-[11.5px] text-[#6B6B67]">{fmtDate(it.target_at)}</span>
                                    </li>
                                ))}
                            </ul>
                        )}
                    </div>
                </Card>
                <Card testid="portal-overview-activity">
                    <CardHeader title="Recent activity" subtle />
                    <div className="p-4">
                        {(!data.recent_activity || !data.recent_activity.length) ? (
                            <div className="text-[13px] text-[#6B6B67]">No recent activity yet.</div>
                        ) : (
                            <ul className="divide-y divide-[#EEEBE4]">
                                {data.recent_activity.slice(0, 6).map((a, i) => (
                                    <li key={i} className="py-2" data-testid={`portal-overview-activity-${i}`}>
                                        <div className="text-[13px] text-charcoal">{a.summary}</div>
                                        <div className="text-[11px] text-[#8A8A84] mt-0.5">{fmtDate(a.at)}</div>
                                    </li>
                                ))}
                            </ul>
                        )}
                    </div>
                </Card>
            </div>
            {/* Phase 6 — Executed documents catch-all placement on
                Overview so clients always have one obvious landing
                for their signed agreement + contract PDFs. */}
            <div className="mt-6" data-testid="portal-overview-executed-docs-wrap">
                <ExecutedDocumentsTile />
            </div>
        </div>
    );
}
